import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ImageRightsAPI } from '../../api/ImageRightsAPI';
import AppPreviewDialog from './AppPreviewDialog';
import {
  CLEARANCE_NOTE, otherUses, otherUsesLabel, removeTitle, sourceLabel, thumbnail, useArmedClick, usageLabel, whenLabel,
} from './pictureParts';

export { CLEARANCE_NOTE };

const FILTERS = [
  { key: 'HIDDEN', label: 'Versteckt' },
  { key: 'CLEARED', label: 'Freigegeben' },
  { key: 'ALL', label: 'Alle' },
];

function PictureTile({ picture, busy, onClear, onRemove, onPreview }) {
  // Without the organiser's consent a picture is cleared only on a second click.
  const [armed, clear] = useArmedClick(() => onClear(picture, true), !picture.accountAgreed);
  const [removeArmed, remove] = useArmedClick(() => onRemove(picture));

  return (
    <article className={'ir-pic' + (picture.cleared ? ' ir-pic--cleared' : '') + (busy ? ' ir-pic--busy' : '')}>
      <a className="ir-pic-image" href={picture.image} target="_blank" rel="noreferrer" title="Original öffnen">
        <img src={thumbnail(picture.image)} alt="" loading="lazy" />
      </a>
      <div className="ir-pic-body">
        <span className={'ir-visibility' + (picture.cleared ? ' is-on' : '')}>{picture.cleared ? 'Freigegeben' : 'Versteckt'}</span>
        {picture.listed && (
          <span className={'isl-badge isl-badge--' + picture.listed.toLowerCase()}>
            {picture.listed === 'WHITE' ? 'Whitelist' : 'Blacklist'} · {picture.listedBy}
          </span>
        )}
        <strong className="ir-pic-title" title={picture.title}>{picture.title}</strong>
        <span className="ir-pic-meta">
          {whenLabel(picture)}
          {picture.account ? `${whenLabel(picture) ? ' · ' : ''}@${picture.account}` : ''}
          {picture.accountAgreed ? ' · Zusage' : ''}
        </span>
        {otherUses(picture).length > 0 && (
          <span className="ir-pic-uses" title={otherUses(picture).map(usageLabel).join('\n')}>{otherUsesLabel(picture)}</span>
        )}
        <a className={'irp-source irp-source--' + (picture.source === 'INSTAGRAM' ? 'ig' : 'web')}
          href={picture.image} target="_blank" rel="noreferrer" title={picture.image}>
          {sourceLabel(picture)}
        </a>
        <button type="button" className="pp-btn" onClick={() => onPreview(picture)}>App-Vorschau</button>
        {picture.cleared ? (
          <button type="button" className="pp-btn" onClick={() => onClear(picture, false)} disabled={busy}>
            Wieder verstecken
          </button>
        ) : picture.listed === 'BLACK' ? (
          <span className="isl-blocked">Blacklist – nicht freigebbar</span>
        ) : (
          <button type="button" className={'pp-btn' + (armed ? ' pp-btn--armed' : '')} onClick={clear} disabled={busy}>
            {armed ? 'Ohne Zusage freigeben?' : 'Freigeben'}
          </button>
        )}
        <button type="button" className={'pp-btn ' + (removeArmed ? 'pp-btn--armed' : 'pp-btn--danger')}
          onClick={remove} disabled={busy} title={removeTitle(picture)}>
          {removeArmed ? 'Wirklich entfernen?' : 'Entfernen'}
        </button>
      </div>
    </article>
  );
}

/** Every picture from elsewhere one by one; each reaches users only once cleared here. */
export default function SinglePictures() {
  const [pictures, setPictures] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('HIDDEN');
  const [hidePast, setHidePast] = useState(true);
  const [busy, setBusy] = useState(() => new Set());
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPictures(await ImageRightsAPI.pictures());
    } catch (err) {
      console.error('Fehler beim Laden der Bilder:', err);
      setError('Konnte die Bilder nicht laden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setClearance = async (picture, cleared) => {
    setBusy((prev) => new Set(prev).add(picture.image));
    setError(null);
    try {
      const result = await ImageRightsAPI.clear([picture.image], cleared, cleared ? CLEARANCE_NOTE : null);
      if (cleared && result?.blocked) {
        setError('Dieses Bild stammt von einer Blacklist-Quelle und bleibt versteckt.');
        return;
      }
      // The same picture can stand on several markers.
      setPictures((prev) => prev && prev.map((each) => (each.image === picture.image
        ? { ...each, cleared, visibleInProd: cleared }
        : each)));
    } catch (err) {
      console.error('Fehler beim Freigeben:', err);
      setError('Konnte das Bild nicht speichern.');
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(picture.image);
        return next;
      });
    }
  };

  const removePicture = async (picture) => {
    setBusy((prev) => new Set(prev).add(picture.image));
    setError(null);
    try {
      await ImageRightsAPI.remove(picture.image);
      setPictures((prev) => prev && prev.filter((each) => each.image !== picture.image));
    } catch (err) {
      console.error('Fehler beim Entfernen:', err);
      setError('Konnte das Bild nicht entfernen.');
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(picture.image);
        return next;
      });
    }
  };

  // Clubs show their picture all year; only events end.
  const isPast = (picture) => picture.type !== 'Club' && !picture.upcoming;
  const inScope = useMemo(() => (pictures ?? []).filter((picture) => !hidePast || !isPast(picture)), [pictures, hidePast]);
  const matches = (picture, key) => {
    if (key === 'HIDDEN') return !picture.cleared;
    if (key === 'CLEARED') return picture.cleared;
    return true;
  };
  const shown = inScope.filter((picture) => matches(picture, filter));

  return (
    <section aria-label="Einzelbilder">
      <p className="ir-rule">
        Jedes Bild wird einzeln freigegeben. Freigeben, wenn der Veranstalter zugestimmt hat – oder wenn im Bild nichts
        Geschütztes steckt: <strong>reine Schrift auf schlichtem Hintergrund</strong>. Fotos sind in Österreich immer
        geschützt (§ 73 UrhG), auch einfache Schnappschüsse – ebenso Illustrationen und gestaltete Grafiken.
      </p>

      <div className="ir-toolbar">
        <div className="ir-filters" role="tablist" aria-label="Filter">
          {FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              aria-selected={filter === option.key}
              className={'ir-chip' + (filter === option.key ? ' is-active' : '')}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
              <span className="ir-chip-count">{inScope.filter((picture) => matches(picture, option.key)).length}</span>
            </button>
          ))}
        </div>
        <div className="ir-toolbar-right">
          <label className="ir-check">
            <input type="checkbox" checked={hidePast} onChange={(event) => setHidePast(event.target.checked)} />
            Vergangene Events ausblenden
          </label>
          <button className="btn-add btn-add--green" type="button" onClick={load} disabled={loading}>
            Neu laden
          </button>
        </div>
      </div>

      {error && <p className="pp-error">{error}</p>}

      {loading && !pictures ? (
        <div className="pp-sentinel"><div className="pp-spinner" aria-label="Lädt" /></div>
      ) : shown.length === 0 ? (
        <div className="pp-empty">Keine Bilder in diesem Filter.</div>
      ) : (
        <div className="ir-grid">
          {shown.map((picture) => (
            <PictureTile
              key={picture.image}
              picture={picture}
              busy={busy.has(picture.image)}
              onClear={setClearance}
              onRemove={removePicture}
              onPreview={setPreview}
            />
          ))}
        </div>
      )}
      {preview && <AppPreviewDialog picture={preview} onClose={() => setPreview(null)} />}
    </section>
  );
}
